import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from './context/UserContext';
import { ProjectDashboard } from './components/ProjectDashboard';
import { ProjectSetup } from './components/wizard/ProjectSetup';
import { ModelSelection } from './components/wizard/ModelSelection';
import { ScriptInput } from './components/wizard/ScriptInput';
import { CharacterSelection } from './components/wizard/CharacterSelection';
import { Branding } from './components/wizard/Branding';
import { TierSelection } from './components/wizard/TierSelection';
import { AutoPilot } from './components/wizard/AutoPilot';
import { WizardProgressRail } from './components/wizard/WizardProgressRail';
import { ConceptStep } from './components/wizard/ConceptStep';
import { CoPilotPanel } from './components/wizard/CoPilotPanel';
import { suggestIndustryFromBrand } from './components/wizard/conceptCatalog';
import { findMatchingBrandSheetName } from './utils/brandProductNameMatch';
import { Storyboard } from './components/Storyboard';
import { Studio } from './components/Studio';
import { VideoFallbackApprovalDialog } from './components/VideoFallbackApprovalDialog';
import { 
    generateScriptFromAudio, 
    generateCharacters, 
    generateStoryboard, 
    mapEntitiesToShot
} from './services/geminiService';
import { getCredits } from './services/aiService';
import { CREDIT_COSTS } from './types';
import { 
    deductCredits, 
    saveProjectToBackend, 
    getProjectsFromBackend, 
    loadProjectFromBackend 
} from './services/backendService';
import { syncLocalProjectsToBackend } from './services/syncLocalProjects';
import { reconcileLocalProjectsWithBackend } from './services/reconcileLocalProjects';
import { 
    saveProjectToIndexedDB, 
    loadProjectFromIndexedDB, 
    deleteProjectFromIndexedDB,
    getAllProjectCredits
} from './services/dbService';
import { MagicWandIcon } from './components/icons/MagicWandIcon';
import { LoadingSpinner } from './components/icons/LoadingSpinner';
import { SaveIcon } from './components/icons/SaveIcon';
import { DownloadIcon } from './components/icons/DownloadIcon';
import './App.css';

const DEFAULT_CREDITS = 10000;

const getVideoDuration = (src) => {
    return new Promise((resolve) => {
        // If source is missing or invalid, return a reasonable default
        if (!src || typeof src !== 'string') {
            console.warn('⚠️ getVideoDuration: Invalid or missing video source');
            resolve(5); // Default 5 seconds for missing source
            return;
        }

        const video = document.createElement('video');
        video.preload = 'metadata';
        video.crossOrigin = 'anonymous';
        
        let resolved = false;
        
        const cleanup = () => {
            video.onloadedmetadata = null;
            video.ondurationchange = null;
            video.onerror = null;
            video.oncanplay = null;
            video.src = '';
            video.load();
        };
        
        const resolveDuration = (duration, source) => {
            if (resolved) return;
            resolved = true;
            console.log(`✅ Video duration from ${source}:`, duration, 'seconds for', src.substring(0, 60) + '...');
            cleanup();
            resolve(duration);
        };

        const onLoaded = () => {
            if (video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                resolveDuration(video.duration, 'loadedmetadata');
            }
        };
        
        const onDurationChange = () => {
            if (video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                resolveDuration(video.duration, 'durationchange');
            }
        };
        
        const onCanPlay = () => {
            if (video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                resolveDuration(video.duration, 'canplay');
            }
        };

        const onError = (e) => {
            console.warn('⚠️ Video metadata load error:', e?.message || 'unknown error', 'for', src.substring(0, 60));
            if (!resolved) {
                resolved = true;
                cleanup();
                resolve(5); // Default 5 seconds on error
            }
        };

        video.onloadedmetadata = onLoaded;
        video.ondurationchange = onDurationChange;
        video.oncanplay = onCanPlay;
        video.onerror = onError;
        
        // Longer timeout for remote videos (8 seconds)
        setTimeout(() => {
            if (!resolved) {
                console.warn('⚠️ Video duration timeout for:', src.substring(0, 60));
                resolved = true;
                cleanup();
                resolve(5); // Default 5 seconds on timeout
            }
        }, 8000);
        
        video.src = src;
        video.load(); // Explicitly trigger loading
    });
};

const createTimelineFromScenes = async (scenes, existingTimeline, branding) => {
    const defaultTracks = [
        { id: 'track-video', type: 'video', name: 'Video' },
        { id: 'track-audio-music', type: 'audio', name: 'Music' },
        { id: 'track-audio-sfx', type: 'audio', name: 'SFX' },
        { id: 'track-text', type: 'text', name: 'Text' },
        { id: 'track-logo', type: 'logo', name: 'Logo' },
        { id: 'track-image', type: 'image', name: 'Image' }
    ];
    // Migrate: if existing timeline is missing logo/image tracks, add them
    let tracks = existingTimeline?.tracks || defaultTracks;
    if (!tracks.find(t => t.type === 'logo')) tracks = [...tracks, { id: 'track-logo', type: 'logo', name: 'Logo' }];
    if (!tracks.find(t => t.type === 'image')) tracks = [...tracks, { id: 'track-image', type: 'image', name: 'Image' }];
    
    // Strip any auto-generated branding clips from a previous run so users can
    // tweak the Branding step and have the timeline regenerate cleanly.
    const nonVideoClips = (existingTimeline?.clips || [])
        .filter(c => c.type !== 'video')
        .filter(c => !c.brandingRole);
    const newVideoClips = [];
    let currentTime = 0;
    const videoTrackId = tracks.find(t => t.type === 'video')?.id || 'track-video';

    for (const scene of scenes) {
        for (const shot of scene.shots) {
            if (shot.video) {
                // Use stored videoDuration if available, otherwise try to get from video element
                let duration;
                if (shot.videoDuration && shot.videoDuration > 0) {
                    duration = shot.videoDuration;
                    console.log(`📹 Using stored duration for shot ${shot.id}:`, duration, 'seconds');
                } else {
                    duration = await getVideoDuration(shot.video);
                    console.log(`📹 Calculated duration for shot ${shot.id}:`, duration, 'seconds');
                }
                
                const videoClip = {
                    id: `clip-video-${shot.id}`,
                    assetId: 'none', 
                    shotId: shot.id,
                    type: 'video',
                    subtype: 'video',
                    trackId: videoTrackId,
                    start: currentTime,
                    duration: duration,
                    sourceStart: 0,
                    sourceDuration: duration,
                    transition: { type: 'none', duration: 0 }
                };
                newVideoClips.push(videoClip);
                currentTime += duration;
            } else {
                if (shot.startFrameImage) {
                    const startClip = {
                        id: `clip-image-start-${shot.id}`,
                        assetId: 'none',
                        shotId: shot.id,
                        type: 'video',
                        subtype: 'start',
                        trackId: videoTrackId,
                        start: currentTime,
                        duration: 2,
                        sourceStart: 0,
                        sourceDuration: 2
                    };
                    newVideoClips.push(startClip);
                    currentTime += 2;
                }
                
                if (shot.endFrameImage) {
                    const endClip = {
                        id: `clip-image-end-${shot.id}`,
                        assetId: 'none',
                        shotId: shot.id,
                        type: 'video',
                        subtype: 'end',
                        trackId: videoTrackId,
                        start: currentTime,
                        duration: 2,
                        sourceStart: 0,
                        sourceDuration: 2
                    };
                    newVideoClips.push(endClip);
                    currentTime += 2;
                }
            }
        }
    }

    const allClips = [...newVideoClips, ...nonVideoClips];
    const maxClipEnd = allClips.reduce((max, clip) => Math.max(max, clip.start + clip.duration), 0);
    const videoDuration = Math.max(maxClipEnd, 0);

    // ---------- Inject branding overlays (logo + outro) ----------
    const brandingClips = [];
    const logoTrackId = tracks.find(t => t.type === 'logo')?.id || 'track-logo';
    const imageTrackId = tracks.find(t => t.type === 'image')?.id || 'track-image';
    const textTrackId = tracks.find(t => t.type === 'text')?.id || 'track-text';

    let totalAfterBranding = videoDuration;

    // Outro extends timeline length so it sits AFTER the video content.
    let outroStart = videoDuration;
    let outroDuration = 0;
    if (branding?.outro?.enabled) {
        outroDuration = Math.max(1, Number(branding.outro.duration) || 3);
        const pos = branding.outro.position || { x: 50, y: 50 };
        const outroWidth = branding.outro.width ?? 60;
        const outroOpacity = branding.outro.opacity ?? 1;
        if (branding.outro.type === 'image' && branding.outro.imageSrc) {
            brandingClips.push({
                id: `clip-image-outro-${Date.now()}`,
                type: 'image',
                trackId: imageTrackId,
                start: outroStart,
                duration: outroDuration,
                assetId: 'branding-outro-image',
                name: branding.outro.imageName || 'Outro image',
                src: branding.outro.imageSrc,
                x: pos.x, y: pos.y, width: outroWidth, height: outroWidth,
                rotation: 0, opacity: outroOpacity,
                brandingRole: 'outro',
            });
        } else if (branding.outro.type === 'text' && branding.outro.text) {
            brandingClips.push({
                id: `clip-text-outro-${Date.now()}`,
                type: 'text',
                trackId: textTrackId,
                start: outroStart,
                duration: outroDuration,
                assetId: 'text-asset',
                content: branding.outro.text,
                fontSize: 14,
                color: '#ffffff',
                fontFamily: 'sans-serif',
                x: pos.x, y: pos.y,
                rotation: 0, opacity: outroOpacity,
                brandingRole: 'outro',
            });
        }
        totalAfterBranding = outroStart + outroDuration;
    }

    // Logo runs over the ENTIRE timeline (incl. outro) starting at 0s.
    if (branding?.logo?.enabled && branding.logo.src && totalAfterBranding > 0) {
        const pos = branding.logo.position || { x: 4, y: 4 };
        brandingClips.push({
            id: `clip-logo-branding-${Date.now()}`,
            type: 'logo',
            trackId: logoTrackId,
            start: 0,
            duration: totalAfterBranding,
            assetId: 'branding-logo',
            name: branding.logo.name || 'Logo',
            src: branding.logo.src,
            x: pos.x, y: pos.y,
            width: branding.logo.width ?? 15,
            height: branding.logo.height ?? 15,
            rotation: 0, opacity: branding.logo.opacity ?? 1,
            brandingRole: 'logo',
        });
    }

    const finalClips = [...allClips, ...brandingClips];
    const totalDuration = Math.max(10, totalAfterBranding + 1);

    // ---------- Auto fade-in / fade-out ----------
    // Applies a 1s fade-in to the first video clip and a 1s fade-out to the
    // last visible item on the timeline (outro if enabled, otherwise the
    // last video clip). Skipped if the item already has a non-'none'
    // transition the user (or a previous run) configured.
    const FADE_DURATION = 1;
    const applyTransition = (clip, type) => {
        if (!clip) return;
        const existing = clip.transition;
        if (existing && existing.type && existing.type !== 'none') return; // respect manual config
        // If we're trying to put both fades on the same clip, upgrade to cross-dissolve.
        if (existing && (existing.type === 'fade-in' || existing.type === 'fade-out') && existing.type !== type) {
            clip.transition = { type: 'cross-dissolve', duration: FADE_DURATION };
        } else {
            clip.transition = { type, duration: FADE_DURATION };
        }
    };

    const videoClips = finalClips.filter(c => c.type === 'video').sort((a, b) => a.start - b.start);
    const firstVideo = videoClips[0];
    const lastVideo = videoClips[videoClips.length - 1];
    const outroClip = finalClips.find(c => c.brandingRole === 'outro');
    const logoClip  = finalClips.find(c => c.brandingRole === 'logo');

    applyTransition(firstVideo, 'fade-in');
    applyTransition(outroClip || lastVideo, 'fade-out');
    // The logo overlay runs over the entire timeline as a single clip — give it
    // a cross-dissolve so it fades in with the first frame and fades out with
    // the last frame instead of popping in/out abruptly.
    if (logoClip && (!logoClip.transition || logoClip.transition.type === 'none')) {
        logoClip.transition = { type: 'cross-dissolve', duration: FADE_DURATION };
    }

    return {
        tracks,
        clips: finalClips,
        duration: totalDuration
    };
};

function App() {
    // Get user data from context
    const { user, apiUrl, loading: userLoading, updateCredits, refreshCredits } = useUser();
    
    const [projects, setProjects] = useState([]);
    const [activeProject, setActiveProject] = useState(null);
    // Phase 5/6.1 (Feb 2026): Co-pilot is a persistent right rail. Default
    // OPEN so users discover it on first run; collapses to a 48px strip
    // when clicked.
    const [coPilotOpen, setCoPilotOpen] = useState(true);
    const [step, setStep] = useState('dashboard');
    const [error, setError] = useState(null);
    const [generationState, setGenerationState] = useState('idle');
    const [generationProgress, setGenerationProgress] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [showCreditsModal, setShowCreditsModal] = useState(false);

    // Poll for credit changes while the credits modal is open
    useEffect(() => {
        if (!showCreditsModal) return;
        const creditsAtOpen = user?.credits || 0;
        const pollId = setInterval(async () => {
            try {
                const serverCredits = await getCredits();
                if (serverCredits > 0 && serverCredits !== creditsAtOpen) {
                    updateCredits(serverCredits);
                    setShowCreditsModal(false);
                }
            } catch { /* ignore */ }
        }, 3000);
        return () => clearInterval(pollId);
    }, [showCreditsModal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Feb 2026 — Persist the active project's chosen video style to
    // localStorage so ALL image-generation calls (character portraits,
    // element sheets, storyboard panel text-to-image, scene images,
    // image-editor refinements) can inject a consistent visual-style
    // directive into their prompts. Read back by
    // `getVideoStyleDirective()` in `aiService.js`. Cleared on
    // project switch / logout.
    useEffect(() => {
        try {
            if (activeProject?.videoStyle) {
                localStorage.setItem('currentProjectVideoStyle', activeProject.videoStyle);
            } else {
                localStorage.removeItem('currentProjectVideoStyle');
            }
        } catch { /* noop */ }
    }, [activeProject?.videoStyle, activeProject?.id]);

    
    // Use credits from user context
    const [creditsUsed, setCreditsUsed] = useState(() => {
        const stored = localStorage.getItem('user_credits_used');
        return stored ? parseInt(stored, 10) : 0;
    });

    // Fetch credits from server on mount if user is authenticated
    useEffect(() => {
        let isMounted = true;
        let hasFetched = false;
        
        const fetchCredits = async () => {
            if (!user?.isAuthenticated || hasFetched) return;
            hasFetched = true;
            
            try {
                const serverCredits = await getCredits();
                if (isMounted && serverCredits > 0) {
                    updateCredits(serverCredits);
                }
            } catch (e) {
                console.log('Using local/context credits (server unavailable)');
            }
        };
        
        // Only fetch once when user becomes authenticated
        if (user?.isAuthenticated) {
            fetchCredits();
        }
        
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.isAuthenticated]); // Remove updateCredits from deps to prevent infinite loop

    // Save credits used to localStorage
    useEffect(() => {
        localStorage.setItem('user_credits_used', creditsUsed.toString());
    }, [creditsUsed]);

    // Load projects when user is loaded
    useEffect(() => {
        if (user && !userLoading) {
            // Load projects IMMEDIATELY so the dashboard renders fast.
            // Then run IDB → canonical sync in the background, and refresh the list
            // once it finishes so any newly-synced projects appear without a page refresh.
            loadProjects();
            if (user.isAuthenticated && !user.isGuest) {
                (async () => {
                    try {
                        const result = await syncLocalProjectsToBackend();
                        if (result?.cleaned > 0) {
                            console.log(`🧹 Cleaned up ${result.cleaned} abandoned empty draft(s)`);
                        }
                        if (result?.tombstoned > 0) {
                            console.log(`🪦 Pruned ${result.tombstoned} tombstoned project(s) deleted from dashboard`);
                        }
                        if (result?.synced > 0) {
                            console.log(`✅ Synced ${result.synced} local draft(s) to canonical backend`);
                            loadProjects();
                        } else if ((result?.cleaned || 0) + (result?.tombstoned || 0) > 0) {
                            // Cleanup/tombstone without new syncs still warrants a dashboard refresh
                            // (those rows may have been appearing as list rows).
                            loadProjects();
                        }
                        if (result?.failed > 0) {
                            console.warn(`⚠️ ${result.failed} local draft(s) failed to sync`);
                        }
                    } catch (err) {
                        console.warn('IDB → backend sync failed (will retry next boot):', err?.message);
                    }
                })();
            }
        }
        // eslint-disable-next-line
    }, [user?.email, userLoading]);

    // Dashboard-route reconciler. Runs every time the user navigates back
    // to the dashboard and whenever the window regains focus. Catches the
    // case where a project was deleted from app.loopai.in in another tab
    // or session — without this, Studio Pro would keep showing the stale
    // card until the next full boot.
    useEffect(() => {
        if (!user || userLoading) return;
        if (!user.isAuthenticated || user.isGuest) return;
        if (step !== 'dashboard') return;

        let cancelled = false;
        const runReconciler = async (source) => {
            try {
                const res = await reconcileLocalProjectsWithBackend();
                if (cancelled) return;
                if (res?.dropped > 0) {
                    console.log(`🪦 Reconciler (${source}) dropped ${res.dropped} tombstoned project(s)`);
                    loadProjects();  // refresh dashboard list
                }
            } catch (err) {
                console.warn(`Reconciler (${source}) failed:`, err?.message);
            }
        };

        runReconciler('dashboard-mount');

        const onFocus = () => runReconciler('window-focus');
        window.addEventListener('focus', onFocus);
        return () => {
            cancelled = true;
            window.removeEventListener('focus', onFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, user?.email, userLoading]);

    const saveProjectToLocal = async (project) => {
        try {
            await saveProjectToIndexedDB(project);
            const listKey = `projects_list_${user?.email || 'guest'}`;
            const existingListStr = localStorage.getItem(listKey);
            let list = existingListStr ? JSON.parse(existingListStr) : [];
            const index = list.findIndex(p => p.id === project.id);
            const metadata = { 
                id: project.id, 
                name: project.name, 
                lastModified: project.lastModified,
                projectId: project.projectId,
                creditsUsed: project.creditsUsed || 0
            };
            if (index >= 0) list[index] = metadata;
            else list.unshift(metadata);
            localStorage.setItem(listKey, JSON.stringify(list));
            return true;
        } catch (e) {
            console.error("Local save failed:", e);
            return false;
        }
    };

    const loadProjectFromLocal = async (id) => {
        try {
            return await loadProjectFromIndexedDB(id);
        } catch (e) {
            console.error("Local load failed:", e);
            return null;
        }
    };

    const loadProjects = async () => {
        setIsLoadingProjects(true);
        const localListKey = `projects_list_${user?.email || 'guest'}`;
        const localListStr = localStorage.getItem(localListKey);
        let allProjects = localListStr ? JSON.parse(localListStr) : [];

        // Pass apiUrl from user context to fetch from correct backend
        const result = await getProjectsFromBackend(user?.email, apiUrl);
        if (result && result.success !== false && Array.isArray(result.projects)) {
            const backendProjects = result.projects;
            // Merge all projects into one array
            const combined = [...allProjects, ...backendProjects];
            // Deduplicate by name — keep the entry with the most recent lastModified
            // This is robust because the backend already has 1 entry per project
            const byName = new Map();
            for (const p of combined) {
                const existing = byName.get(p.name);
                if (!existing) {
                    byName.set(p.name, p);
                } else {
                    // Keep the one with more data: prefer higher creditsUsed, then newer timestamp
                    const existCredits = existing.creditsUsed || 0;
                    const newCredits = p.creditsUsed || 0;
                    if (newCredits > existCredits || 
                        (newCredits === existCredits && (p.lastModified || 0) > (existing.lastModified || 0))) {
                        byName.set(p.name, { ...existing, ...p, creditsUsed: Math.max(existCredits, newCredits) });
                    } else {
                        byName.set(p.name, { ...p, ...existing, creditsUsed: Math.max(existCredits, newCredits) });
                    }
                }
            }
            allProjects = Array.from(byName.values()).sort((a, b) => (b.lastModified || 0) - (a.lastModified || 0));
        }

        // Enrich with creditsUsed from IndexedDB (full project data has the real value)
        try {
            const creditMap = await getAllProjectCredits();
            allProjects = allProjects.map(p => ({
                ...p,
                creditsUsed: creditMap.get(p.id) || p.creditsUsed || 0
            }));
        } catch { /* ignore — credits will show 0 */ }

        setProjects(allProjects);
        setIsLoadingProjects(false);
    };

    const handleDeductCredits = (amount) => {
        if (!user?.credits || user.credits < amount) {
            alert("Not enough credits!");
            return false;
        }
        if (user.credits >= amount) {
            const newCredits = user.credits - amount;
            updateCredits(newCredits);
            setCreditsUsed(prev => prev + amount);
            if (activeProject) {
                setActiveProject(prev => (prev ? { ...prev, creditsUsed: (prev.creditsUsed || 0) + amount } : prev));
                deductCredits(user?.email, amount, activeProject.projectId || activeProject.id, activeProject.name);
            }
            return true;
        }
        alert("Not enough credits!");
        return false;
    };

    // Track credits used after a successful API call (uses actual credits from backend response)
    const handleTrackCreditsUsed = async (creditsUsed, creditsRemaining, jobId) => {
        if (!creditsUsed || creditsUsed <= 0) return;
        
        console.log(`💳 Credits used: ${creditsUsed}, Remaining: ${creditsRemaining || 'unknown'}, Job ID: ${jobId || 'none'}`);
        
        // Update session total (localStorage-backed) — this one is a simple counter, safe.
        setCreditsUsed(prev => prev + creditsUsed);

        // Project total = sum of what the backend reports per job (functional update is
        // safe under parallel batches; balance refreshes can't inflate it).
        setActiveProject(prev => (prev ? { ...prev, creditsUsed: (prev.creditsUsed || 0) + creditsUsed } : prev));

        // Record jobId on the active project (still useful for the backend credits audit).
        if (jobId) {
            setActiveProject(prev => {
                if (!prev) return prev;
                const prevJobIds = prev.jobIds || [];
                return { ...prev, jobIds: [...prevJobIds, jobId] };
            });
        }

        // Update credits from creditsRemaining if available, otherwise refresh from backend.
        // NOTE: project `creditsUsed` is NOT touched here. It is derived by
        // observing `user.credits` transitions below — that eliminates the
        // race conditions and accounting drift we saw under parallel batch.
        if (creditsRemaining !== undefined && creditsRemaining !== null) {
            updateCredits(creditsRemaining);
        } else if (refreshCredits) {
            await refreshCredits();
        }
    };

    // ---- Balance audit (diagnostic only; does NOT change project creditsUsed) ----
    // Logs when the user-backend balance drops by more than the jobs reported,
    // which points at a backend-side pricing / double-charge issue.
    const lastObservedCreditsRef = useRef(null);
    useEffect(() => {
        const current = user?.credits;
        if (current === undefined || current === null) return;
        const last = lastObservedCreditsRef.current;
        lastObservedCreditsRef.current = current;
        if (last === null || last === undefined || current >= last) return;
        console.log(`💳 Balance audit: user credits ${last} → ${current} (−${last - current})`);
    }, [user?.credits]);

    // Fetch project credits total from backend
    const fetchProjectCredits = async () => {
        if (!activeProject) return;
        
        try {
            const { getProjectCredits } = await import('./services/aiService');
            const projectId = activeProject.projectId || activeProject.id;
            const jobIds = activeProject.jobIds || [];
            
            const result = await getProjectCredits(projectId, jobIds);
            
            if (result.success) {
                console.log(`📊 Project credits: ${result.totalCreditsUsed} used, ${result.availableCredits} available`);
                
                // Update project credits
                setActiveProject(prev => ({
                    ...prev,
                    creditsUsed: result.totalCreditsUsed
                }));
                
                // Update available credits
                updateCredits(result.availableCredits);
            }
        } catch (error) {
            console.error('Failed to fetch project credits:', error);
        }
    };

    const handleSaveMedia = async (mediaData) => {
        if (!activeProject) return;
        // DEPRECATED (Apr 2026 migration): the legacy /api/webhook/save-images endpoint
        // wrote thin stubs to the obsolete db.projects collection. Canonical project state
        // is now persisted via handleSaveProject → POST /api/video-projects/save.
        // Image URLs from generation are already on storage.googleapis.com (server-side
        // CDN→GCS persistence shipped Apr 25), so they survive a save without this hop.
        // Intentional no-op; kept as a hook in case future code wants per-batch hooks.
        void mediaData;
    };

    // Shared handler for HTTP 410 (Gone) from save endpoints.
    // Backend has tombstoned this project (user deleted it from the dashboard)
    // and will reject all future saves for that project_id. We drop the IDB
    // row, remove it from the in-memory list, clear the editor if it's the
    // current active project, and return the user to the dashboard.
    const handleProjectGone = async (goneProject, reason) => {
        const goneLocalId = goneProject.id;
        const goneBackendId = goneProject.projectId;
        console.warn(`🪦 Project tombstoned, pruning locally. localId=${goneLocalId} projectId=${goneBackendId}`);
        try { await deleteProjectFromIndexedDB(goneLocalId); } catch { /* noop */ }
        try {
            const listKey = `projects_list_${user?.email || 'guest'}`;
            const list = JSON.parse(localStorage.getItem(listKey) || '[]');
            localStorage.setItem(listKey, JSON.stringify(list.filter((p) => p.id !== goneLocalId)));
        } catch { /* noop */ }
        setProjects(prev => prev.filter(p => p.id !== goneLocalId));
        // If the user is currently editing the tombstoned project, bounce
        // them to the dashboard — we can't keep editing a deleted project.
        if (activeProjectRef.current?.id === goneLocalId) {
            setActiveProject(null);
            setStep('dashboard');
            // eslint-disable-next-line no-alert
            alert(`"${goneProject.name || 'This project'}" was deleted from your account. Removed from Studio Pro.`);
        }
        void reason;  // reserved for future analytics hook
    };

    const handleSaveProject = async () => {
        if (!activeProject) return;
        setIsSaving(true);
        const updatedProject = { ...activeProject, lastModified: Date.now(), currentStep: step };
        setActiveProject(updatedProject);
        await saveProjectToLocal(updatedProject);
        // Pass apiUrl from user context to save to correct backend
        const result = await saveProjectToBackend(updatedProject, user?.email, apiUrl);
        if (result?.gone) {
            await handleProjectGone(updatedProject, 'manual-save');
            setIsSaving(false);
            return;
        }
        if (result?.success !== false && result?.project_id) {
            // Stamp syncedToBackend/syncedAt so the dashboard reconciler
            // knows this project has been observed by the backend at
            // least once, and can safely tombstone-prune it later.
            const projectWithId = {
                ...updatedProject,
                projectId: result.project_id,
                syncedToBackend: true,
                syncedAt: Date.now()
            };
            setActiveProject(projectWithId);
            await saveProjectToLocal(projectWithId);
            if (result.project_id !== updatedProject.projectId) {
                setProjects(prev => prev.map(p => p.id === projectWithId.id ? { ...p, projectId: result.project_id } : p));
            }
        }
        setIsSaving(false);
    };

    // Auto-save: silent, non-blocking, coalesced. Used at critical checkpoints
    // (script→characters, characters→storyboard, pre-prod complete, frames
    // done, videos done, storyboard→studio, render click, single-shot save,
    // studio timeline edits, beforeunload) so a user never loses work to a
    // forgotten manual Save. Shows a subtle indicator instead of a toast.
    const activeProjectRef = useRef(null);
    useEffect(() => { activeProjectRef.current = activeProject; }, [activeProject]);

    const stepRef = useRef(step);
    useEffect(() => { stepRef.current = step; }, [step]);

    const [saveIndicator, setSaveIndicator] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
    const autoSaveInFlightRef = useRef(false);
    const autoSavePendingRef = useRef(false);

    const autoSaveProject = async (opts = {}) => {
        const { localOnly = false, reason = '', projectOverride = null } = opts;
        // Prefer an explicit project override (passed by callers that just
        // constructed a new project object) — this avoids a race with React's
        // async setState + the activeProjectRef useEffect not yet firing.
        const latest = projectOverride || activeProjectRef.current;
        if (!latest) return;
        // Coalesce concurrent triggers — if one save is in flight, queue a
        // single follow-up and let that pick up the newest state.
        if (autoSaveInFlightRef.current) {
            autoSavePendingRef.current = true;
            return;
        }
        autoSaveInFlightRef.current = true;
        setSaveIndicator('saving');
        try {
            // IMPORTANT: Never let a stale `projectOverride` regress
            // `creditsUsed`. The credits observer (watching user.credits
            // transitions) may have already incremented the live
            // activeProject's creditsUsed AFTER the caller captured their
            // override snapshot. Take the max to preserve the observer's
            // increments.
            const liveRefCreditsUsed = activeProjectRef.current?.creditsUsed || 0;
            const overrideCreditsUsed = latest.creditsUsed || 0;
            // Prefer the live ref's projectId over the override's. The
            // override may be a stale snapshot taken before a previous
            // save's response wrote `project_id` back into activeProject.
            // Without this, a follow-up save would POST projectId=null
            // and the backend would create a duplicate row.
            const liveRefProjectId = activeProjectRef.current?.projectId;
            const overrideProjectId = latest.projectId;
            const updated = {
                ...latest,
                projectId: liveRefProjectId || overrideProjectId || null,
                creditsUsed: Math.max(overrideCreditsUsed, liveRefCreditsUsed),
                // Also preserve jobIds — handleTrackCreditsUsed appends job ids
                // to the live activeProject that an override may not carry.
                jobIds: (() => {
                    const a = latest.jobIds || [];
                    const b = activeProjectRef.current?.jobIds || [];
                    return Array.from(new Set([...a, ...b]));
                })(),
                lastModified: Date.now(),
                currentStep: stepRef.current
            };
            setActiveProject(prev => prev?.id === updated.id ? updated : prev);
            await saveProjectToLocal(updated);
            if (!localOnly) {
                const result = await saveProjectToBackend(updated, user?.email, apiUrl);
                if (result?.gone) {
                    await handleProjectGone(updated, `autosave:${reason}`);
                    setSaveIndicator('idle');
                    return;  // don't treat as 'saved'
                }
                if (result?.success !== false && result?.project_id) {
                    // Stamp syncedToBackend/syncedAt so the dashboard
                    // reconciler can safely prune this row later if the
                    // backend tombstones it.
                    const projectWithId = {
                        ...updated,
                        projectId: result.project_id,
                        syncedToBackend: true,
                        syncedAt: Date.now()
                    };
                    setActiveProject(prev => prev?.id === projectWithId.id ? projectWithId : prev);
                    await saveProjectToLocal(projectWithId);
                    if (result.project_id !== updated.projectId) {
                        setProjects(prev => prev.map(p => p.id === projectWithId.id ? { ...p, projectId: result.project_id } : p));
                    }
                }
            }
            console.log(`💾 Auto-saved${reason ? ` (${reason})` : ''} creditsUsed=${updated.creditsUsed}`);
            setSaveIndicator('saved');
            setTimeout(() => setSaveIndicator(prev => prev === 'saved' ? 'idle' : prev), 2500);
        } catch (err) {
            console.warn('⚠️ Auto-save failed:', err?.message || err);
            setSaveIndicator('error');
            setTimeout(() => setSaveIndicator(prev => prev === 'error' ? 'idle' : prev), 4000);
        } finally {
            autoSaveInFlightRef.current = false;
            if (autoSavePendingRef.current) {
                autoSavePendingRef.current = false;
                setTimeout(() => autoSaveProject({ localOnly, reason: reason + '(followup)' }), 100);
            }
        }
    };

    // Stage 11: save to IDB (local only — network is unreliable during unload)
    // whenever the user is about to leave the page with an active project.
    useEffect(() => {
        const handleBeforeUnload = () => {
            const latest = activeProjectRef.current;
            if (!latest) return;
            try {
                // Fire-and-forget IDB save. Browsers allow sync-ish writes
                // during beforeunload; the network save would be aborted.
                saveProjectToLocal({ ...latest, lastModified: Date.now(), currentStep: stepRef.current });
            } catch { /* noop */ }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCreateProject = () => {
        setActiveProject({
            id: uuidv4(),
            name: '',
            // ─── Phase 1 (Feb 2026) Smart Defaults ───
            // Users land on every screen with sensible pre-selections instead
            // of empty forms. The defaults below are the "most-picked" values
            // observed in production:
            //   30s duration   — most common short-form length
            //   16:9 ratio     — most-used landscape format
            //   realistic style— most common photographic look
            duration: '30 seconds',
            aspectRatio: '16:9',
            videoStyle: 'realistic',
            script: '',
            characters: [],
            scenes: [],
            assets: [],
            lastModified: Date.now(),
            modelPreferences: {},  // Will be set during model selection step
            currentStep: 'project',
            // NEW (Feb 2026): brand-new projects use the panel-based storyboard
            // (one image per panel from the shot's 4-6 panel description) instead
            // of the legacy start-frame + end-frame layout. Old projects loaded
            // from the dashboard do NOT have this flag set, so they keep their
            // original Start Frame / End Frame UI to avoid breaking saved data.
            usesPanelImages: true
        });
        setStep('project');
    };

    const handleLoadProject = async (id) => {
        setGenerationProgress('Loading project...');
        setGenerationState('transcribing');
        const meta = projects.find(p => p.id === id);
        const backendIdToUse = meta?.projectId || id;
        // Pass apiUrl from user context to load from correct backend
        const backendResult = await loadProjectFromBackend(backendIdToUse, user?.email, apiUrl);
        let projectData = null;
        if (backendResult && backendResult.success && backendResult.project) {
            projectData = backendResult.project;
            if (projectData && !projectData.projectId && meta?.projectId) projectData.projectId = meta.projectId;
        } else {
            projectData = await loadProjectFromLocal(id);
        }
        if (projectData) {
            // Feb 13 2026 — Heal projects that were saved with `assets` as
            // an object `{logos,products,world}` (a schema bug from an
            // earlier version of handleBrandScopeApply). Legacy shape had
            // no consumers anyway; the canonical shape is a flat array.
            if (projectData.assets && !Array.isArray(projectData.assets)) {
                console.warn('[App] Healing project.assets from object → [] (legacy brand-scope shape)');
                projectData.assets = [];
            }
            setActiveProject(projectData);
            if (projectData.currentStep) setStep(projectData.currentStep);
            else if (projectData.scenes?.length > 0) setStep(projectData.timeline?.clips.length > 0 ? 'studio' : 'storyboard');
            else if (projectData.characters?.length > 0) setStep('characters');
            else if (projectData.script) setStep('script');
            else setStep('project');
        }
        setGenerationState('idle');
    };

    const handleDeleteProject = async (id) => {
        setProjects(prev => prev.filter(p => p.id !== id));
        const listKey = `projects_list_${user?.email || 'guest'}`;
        const list = JSON.parse(localStorage.getItem(listKey) || '[]');
        localStorage.setItem(listKey, JSON.stringify(list.filter((p) => p.id !== id)));
        await deleteProjectFromIndexedDB(id);
    };

    const handleProjectSetupSubmit = (data) => {
        if (!activeProject) return;
        // Feb 2026 — Re-enabled the AI Toolkit ("Select Your AI Toolkit")
        // step immediately after Step 1 (Goal). Users explicitly asked to
        // pick their AI models up-front rather than defaulting silently.
        setActiveProject({ ...activeProject, ...data, lastModified: Date.now(), currentStep: 'models' });
        setStep('models');
    };

    const handleModelSelectionSubmit = (data) => {
        if (!activeProject) return;
        setActiveProject({ ...activeProject, modelPreferences: data, lastModified: Date.now(), currentStep: 'script' });
        setStep('script');
    };

    const handleScriptSubmit = async (data) => {
        if (!activeProject) return;
        setError(null);
        // Phase 2 (Feb 2026) split: when the user picks "I'm not sure —
        // give me ideas" on the Story step, ScriptInput emits
        // `{ goToConcept: true, ...storyMeta }` instead of a script. We
        // stash the story-level meta on the project and advance to the
        // new dedicated Concept step.
        if (data?.goToConcept) {
            const updated = {
                ...activeProject,
                lastModified: Date.now(),
                currentStep: 'concept',
            };
            if (data.brandName) updated.brandName = data.brandName;
            if (data.audioStyle) updated.audioStyle = data.audioStyle;
            if (data.videoStyle) updated.videoStyle = data.videoStyle;
            if (data.castMode) updated.castMode = data.castMode;
            if (data.industryId) updated.industryId = data.industryId;
            if (data.videoTypeId) updated.videoTypeId = data.videoTypeId;
            if (data.customVideoTypeName) updated.customVideoTypeName = data.customVideoTypeName;
            if (data.customIndustryName) updated.customIndustryName = data.customIndustryName;
            // Brand scope (Feb 2026)
            if (data.brandId    !== undefined) updated.brandId    = data.brandId;
            if (data.productIds !== undefined) updated.productIds = data.productIds;
            if (data.serviceIds !== undefined) updated.serviceIds = data.serviceIds;
            setActiveProject(updated);
            setStep('concept');
            return;
        }
        let scriptText = '';
        let scriptResultCredits = 0;
        try {
            if (data.script !== undefined) {
                scriptText = data.script;
            }
            else if (data.audio) {
                setGenerationState('transcribing');
                // Credits are deducted by backend - removed upfront deduction
                const scriptResult = await generateScriptFromAudio(data.audio.data);
                scriptText = scriptResult?.script || scriptResult;
                scriptResultCredits = scriptResult?.credits_used || 0;
                if (scriptResult?.credits_used) {
                    handleTrackCreditsUsed(scriptResult.credits_used);
                }
            }
            // Carry this step's job credits in the replacement object (the queued +credits from
            // handleTrackCreditsUsed would otherwise be overwritten by this stale-closure copy).
            const updatedProject = { ...activeProject, creditsUsed: (activeProject?.creditsUsed || 0) + (Number(scriptResultCredits) || 0), script: scriptText, scenes: [], timeline: undefined, lastModified: Date.now(), currentStep: 'characters' };
            if (data.brandName) updatedProject.brandName = data.brandName;
            if (data.audioStyle) updatedProject.audioStyle = data.audioStyle;
            if (data.videoStyle) updatedProject.videoStyle = data.videoStyle;
            if (data.castMode) updatedProject.castMode = data.castMode;
            if (data.industryId) updatedProject.industryId = data.industryId;
            if (data.videoTypeId) updatedProject.videoTypeId = data.videoTypeId;
            if (data.customVideoTypeName) updatedProject.customVideoTypeName = data.customVideoTypeName;
            if (data.customIndustryName) updatedProject.customIndustryName = data.customIndustryName;
            setActiveProject(updatedProject);
            if (scriptText.trim() === '') {
                setStep('characters');
                return;
            }

            // Product-only cast mode: no characters on screen, but off-screen
            // narration is still allowed. Call generateCharacters so a narrator
            // gets created, then strip out everything except narrators. The
            // user lands on Cast & World with only narrator(s) present — they
            // can still add product "elements" manually.
            const productOnlyCast = data.castMode === 'product_only';

            setGenerationState('character-generating');
            
            // Credits are deducted by backend - removed upfront deduction
            
            console.log('🎭 Generating characters from script...', { scriptLength: scriptText.length });
            const storyElementsData = await generateCharacters(scriptText);
            
            // Track credits from character generation
            if (storyElementsData?.credits_used) {
                handleTrackCreditsUsed(storyElementsData.credits_used);
            }
            
            console.log('🎭 Raw characters response:', JSON.stringify(storyElementsData, null, 2));
            console.log('🎭 Characters received:', { 
                type: typeof storyElementsData,
                isArray: Array.isArray(storyElementsData), 
                hasCharacters: !!storyElementsData?.characters,
                hasOtherElements: !!storyElementsData?.otherElements,
                data: storyElementsData 
            });
            
            // Handle the new response format: { characters, otherElements, narrators, allItems }
            let charactersArray = [];
            let otherElementsArray = [];
            let narratorsArray = [];
            
            if (storyElementsData && typeof storyElementsData === 'object') {
                // New format: object with characters, otherElements, and narrators
                if (Array.isArray(storyElementsData.characters)) {
                    charactersArray = storyElementsData.characters;
                }
                if (Array.isArray(storyElementsData.otherElements)) {
                    otherElementsArray = storyElementsData.otherElements;
                }
                if (Array.isArray(storyElementsData.narrators)) {
                    narratorsArray = storyElementsData.narrators;
                }
                // Fallback to allItems if not found
                if (charactersArray.length === 0 && Array.isArray(storyElementsData.allItems)) {
                    charactersArray = storyElementsData.allItems.filter(item => item.category === 'character');
                    otherElementsArray = storyElementsData.allItems.filter(item => item.category === 'element');
                    narratorsArray = storyElementsData.allItems.filter(item => item.category === 'narrator');
                }
            } else if (Array.isArray(storyElementsData)) {
                // Legacy format: direct array - filter by category
                charactersArray = storyElementsData.filter(item => item.category === 'character');
                otherElementsArray = storyElementsData.filter(item => item.category === 'element');
                narratorsArray = storyElementsData.filter(item => item.category === 'narrator');
            } else if (!storyElementsData) {
                console.warn('⚠️ No character data received - using empty array');
                charactersArray = [];
                otherElementsArray = [];
                narratorsArray = [];
            } else {
                console.error('❌ Invalid characters data received:', storyElementsData);
                throw new Error('Invalid character data received from backend');
            }
            
            console.log('🎭 Processed arrays:', { 
                charactersCount: charactersArray.length, 
                otherElementsCount: otherElementsArray.length,
                narratorsCount: narratorsArray.length,
                characters: charactersArray,
                otherElements: otherElementsArray,
                narrators: narratorsArray
            });
            
            const elementsWithIds = charactersArray.map((el, index) => ({
                id: `el-${Date.now()}-${index}`,
                name: el.name,
                description: el.description,
                category: el.category || 'character',
                makeupLevel: 'natural',
            }));
            
            const otherElementsWithIds = otherElementsArray.map((el, index) => ({
                id: `elem-${Date.now()}-${index}`,
                name: el.name,
                description: el.description,
                category: el.category || 'element',
            }));
            
            const narratorsWithIds = narratorsArray.map((el, index) => ({
                id: `narr-${Date.now()}-${index}`,
                name: el.name,
                description: el.description,
                category: 'narrator',
                makeupLevel: 'natural',
            }));
            
            // Fallback: if no narrators found, scan the script for narrator mentions
            if (narratorsWithIds.length === 0 && scriptText) {
                const narratorPatterns = [
                    /\*{0,2}NARRATOR\s*\(([^)]+)\)\*{0,2}/gi,  // **NARRATOR(Willow)**
                    /\*{0,2}NARRATOR\s*[-:]\s*([^\n*]+)/gi,      // **NARRATOR: Willow** or NARRATOR - Willow
                    /\*{0,2}NARRATOR\*{0,2}\s*\n+\s*\*{0,2}(?:Name|Type|Gender)[:\s]*\*{0,2}\s*([^\n]+)/gi // NARRATOR section with Name field
                ];
                
                let narratorName = null;
                let narratorDesc = '';
                
                for (const pattern of narratorPatterns) {
                    const match = pattern.exec(scriptText);
                    if (match) {
                        narratorName = match[1]?.trim();
                        break;
                    }
                }
                
                // Also try to find just "NARRATOR" as a standalone section header
                if (!narratorName && /\bNARRATOR\b/i.test(scriptText)) {
                    narratorName = 'Narrator';
                }
                
                if (narratorName) {
                    // Extract description from nearby text
                    const narratorSection = scriptText.match(/NARRATOR[\s\S]*?(?=\n\s*\n\s*\n|\n\s*---|\n\s*\*\*[A-Z]|$)/i);
                    if (narratorSection) {
                        narratorDesc = narratorSection[0]
                            .replace(/\*+/g, '')
                            .replace(/NARRATOR\s*\([^)]*\)/i, '')
                            .replace(/NARRATOR/i, '')
                            .split('\n')
                            .map(l => l.trim())
                            .filter(l => l && !l.startsWith('---'))
                            .join('. ')
                            .substring(0, 300);
                    }
                    
                    const autoNarrator = {
                        id: `narr-auto-${Date.now()}`,
                        name: narratorName === 'Narrator' ? 'Narrator' : `Narrator (${narratorName})`,
                        description: narratorDesc || `Narrator voice for the story.`,
                        category: 'narrator',
                        makeupLevel: 'natural',
                    };
                    narratorsWithIds.push(autoNarrator);
                    console.log('🎙️ Auto-extracted narrator from script:', autoNarrator.name);
                }
            }

            // Product-only cast mode: drop on-screen characters from the
            // generated cast, but KEEP elements (props, locations, the
            // product itself) and narrators (off-screen voiceover). Cast &
            // World still shows everything except the actors.
            const finalElements = productOnlyCast ? [] : elementsWithIds;
            if (productOnlyCast) {
                console.log(`🚫 castMode=product_only — dropping ${elementsWithIds.length} on-screen character(s); keeping ${otherElementsWithIds.length} element(s) and ${narratorsWithIds.length} narrator(s)`);
            }

            // Feb 13 2026 — Preserve brand-sourced product-sheet elements
            // (added by BrandScopePicker via handleBrandScopeApply) across
            // the script → cast regeneration. Without this preservation
            // step, `project.characters` gets fully overwritten by the
            // freshly-generated cast, silently dropping the Product Sheets
            // the user selected in the Brand Scope.
            // Feb 13 2026 — Preserve brand-sourced elements (product-sheet
            // AND brand-logo — added by BrandScopePicker via
            // handleBrandScopeApply) across the script → cast regeneration.
            // Without this preservation step, `project.characters` gets
            // fully overwritten by the freshly-generated cast, silently
            // dropping the Product Sheets / Logo the user selected in the
            // Brand Scope.
            //
            // Feb 20 2026 — When the LLM ALSO generates an element with a
            // matching product name (e.g. "Flyer 14T Bicycle") OR logo
            // name (e.g. "gro Logo"), the brand-sourced entry (with the
            // fetched image) supersedes the LLM-generated duplicate. Match
            // is a fuzzy TOKEN-based comparison (see
            // `utils/brandProductNameMatch.js`).
            const priorBrandSheetChars = Array.isArray(activeProject?.characters)
                ? activeProject.characters.filter(c => c?.source === 'brand-product-sheet' || c?.source === 'brand-logo')
                : [];
            if (priorBrandSheetChars.length > 0) {
                console.log(`🛒 Preserving ${priorBrandSheetChars.length} brand-sourced element(s) across script submission`);
            }

            const brandSheetNames = priorBrandSheetChars.map(c => c.name).filter(Boolean);

            // Absorb LLM descriptions into matching brand-sheet entries and
            // remove the duplicates from otherElementsWithIds / finalElements.
            const llmDescByBrandName = new Map();
            const collectAndFilterDuplicate = (arr) => arr.filter((el) => {
                const match = findMatchingBrandSheetName(el?.name, brandSheetNames);
                if (match) {
                    if (el.description && !llmDescByBrandName.has(match)) {
                        llmDescByBrandName.set(match, el.description);
                    }
                    console.log(`🧹 Dropping LLM-generated duplicate of brand asset: "${el.name}" → supersedes "${match}"`);
                    return false;
                }
                return true;
            });
            const dedupedOtherElements = collectAndFilterDuplicate(otherElementsWithIds);
            const dedupedFinalElements = collectAndFilterDuplicate(finalElements);

            const enrichedBrandSheetChars = priorBrandSheetChars.map((c) => {
                const llmDesc = c.name ? llmDescByBrandName.get(c.name) : null;
                if (!llmDesc) return c;
                // Only overwrite the auto-generated placeholder descriptions.
                // If the user has already edited the description, preserve it.
                const isPlaceholder = typeof c.description === 'string'
                    && /(product sheet at .*\. Loaded from saved brand\.|brand logo loaded from saved brand\.)/i.test(c.description);
                if (isPlaceholder || !c.description) {
                    return { ...c, description: llmDesc };
                }
                return c;
            });

            // Combine all characters (including narrators) for the characters state.
            // Brand sheets come FIRST so they show up at the top of Props & Elements.
            const combinedChars      = [...enrichedBrandSheetChars, ...dedupedFinalElements, ...dedupedOtherElements, ...narratorsWithIds];
            // De-dupe by id (brand sheets use `product-sheet-${productId}` ids;
            // generated ones use timestamp-based ids so collisions are unlikely
            // but we defensively keep the first occurrence).
            const seenIds            = new Set();
            const allCharactersWithIds = combinedChars.filter(c => {
                if (!c?.id) return true;
                if (seenIds.has(c.id)) return false;
                seenIds.add(c.id);
                return true;
            });

            console.log('🎭 Characters with IDs:', elementsWithIds);
            console.log('🎨 Other Elements with IDs:', otherElementsWithIds);
            console.log('🎙️ Narrators with IDs:', narratorsWithIds);
            console.log('🛒 Brand-product-sheet elements preserved:', priorBrandSheetChars);
            
            // Compute the full updated project once and use it for BOTH the
            // state update and the autosave override. This avoids reading a
            // stale `activeProjectRef.current` when autosave fires before
            // React commits the new state.
            // NOTE: `activeProject` here refers to the closure value at the
            // start of this handler; the script was already merged into state
            // at line ~662 via a prior `setActiveProject`. We compose the
            // autosave snapshot from that same merged value.
            const projectAfterCharacters = updatedProject ? {
                ...updatedProject,
                creditsUsed: (updatedProject.creditsUsed || 0) + (Number(storyElementsData?.credits_used) || 0),
                script: scriptText,
                characters: allCharactersWithIds,
                scenes: [],
                timeline: undefined,
                currentStep: 'characters',
                lastModified: Date.now()
            } : null;
            setActiveProject(prev => (prev ? {
                ...prev,
                characters: allCharactersWithIds,
                currentStep: 'characters'
            } : null));
            setStep('characters');
            // Stage 1: auto-save after script generation + character generation
            if (projectAfterCharacters) {
                setTimeout(() => autoSaveProject({ reason: 'script→characters', projectOverride: projectAfterCharacters }), 0);
            }
        } catch (err) {
            console.error('❌ Error in handleScriptSubmit:', err);
            setError(err.message || 'Generation failed.');
            setStep('script');
        } finally {
            setGenerationState('idle');
        }
    };

    // Phase 2 (Feb 2026) — When the user finishes picking a concept on the
    // dedicated Concept step, ConceptStep generates the script and forwards
    // it here. We funnel it through `handleScriptSubmit` so the existing
    // "script → generate characters → route to Cast" pipeline runs untouched.
    const handleConceptScriptReady = (data) => {
        handleScriptSubmit(data);
    };

    // Brand scope (Feb 2026) — When the user picks a saved brand + products
    // on the Story step, BrandScopePicker fires this with the brand context
    // and (optionally) a hydrated cast_and_world payload. We persist the
    // scope on activeProject AND merge the hydrated assets into
    // `activeProject.assets` so the Cast & World step already shows the
    // brand logo and product images.
    const handleBrandScopeApply = ({ brandId, productIds, serviceIds, castAndWorld, brand, pickedProductsFull, pickedServicesFull, productsMissingSheet }) => {
        setActiveProject(prev => {
            if (!prev) return prev;
            // Feb 2026 — Auto-select the closest Industry from the saved
            // brand so the user doesn't have to pick it again in Step 3.
            // Only fills when the project doesn't already have an industry
            // set (respect any manual choice the user made earlier).
            let nextIndustryId          = prev.industryId;
            let nextIndustryAutoFromBrand = prev.industryAutoFromBrand || false;
            if (brand && !prev.industryId) {
                const { industry: suggested, confidence } = suggestIndustryFromBrand(brand);
                if (suggested && confidence > 0) {
                    nextIndustryId          = suggested.id;
                    nextIndustryAutoFromBrand = true;
                    console.info('[App] Auto-selected industry from brand →', suggested.id, `(confidence ${confidence.toFixed(2)})`);
                }
            }
            // Feb 13 2026 — Product sheets from the saved brand are loaded
            // as `category:"element"` entries in `project.characters[]` so
            // they appear in Cast & World → Props & Elements. Each entry:
            //   { id, name, category:'element', image, startImage, source:'brand-product-sheet', ... }
            // Entries are keyed by `product-sheet-${productId}` so re-applying
            // the same brand doesn't create duplicates. Products removed
            // from the brand scope are also removed from the elements list
            // on re-apply. Manually-added elements (not tagged with
            // source:'brand-product-sheet') are always preserved.
            //
            // Feb 20 2026 — The saved brand's LOGO(S) are also injected
            // as `source:'brand-logo'` elements so the fetched logo image
            // shows up under the "${brand} Logo" card in Props & Elements
            // (previously the LLM created "gro Logo" but the fetched image
            // never reached the element). Logo entries are keyed by
            // `brand-logo-${logoId}` and superseded on re-apply the same
            // way product sheets are.
            const sheetEntries = Array.isArray(castAndWorld?.products) ? castAndWorld.products : [];
            const logoEntries  = Array.isArray(castAndWorld?.logos)    ? castAndWorld.logos    : [];
            const activeProductIds = new Set((productIds || []).filter(Boolean));
            const priorCharacters  = Array.isArray(prev.characters) ? prev.characters : [];
            // Non-brand entries to keep: everything that isn't a brand-sourced
            // sheet/logo, plus brand product-sheets whose products are still
            // in scope (so we don't drop the entry mid-edit).
            const nonBrandChars    = priorCharacters.filter(c => {
                if (c?.source === 'brand-product-sheet') return activeProductIds.has(c?.product_id);
                if (c?.source === 'brand-logo')          return true;
                return true;
            });
            const existingBrandCharsById = new Map(
                nonBrandChars
                    .filter(c => c?.source === 'brand-product-sheet' && c?.product_id)
                    .map(c => [c.product_id, c])
            );
            const existingBrandLogosById = new Map(
                nonBrandChars
                    .filter(c => c?.source === 'brand-logo' && c?.logo_id)
                    .map(c => [c.logo_id, c])
            );
            const brandCharacters = sheetEntries.map(sheet => {
                const productId = sheet.product_id;
                const existing = existingBrandCharsById.get(productId) || {};
                return {
                    ...existing,
                    id:            existing.id || `product-sheet-${productId}`,
                    product_id:    productId,
                    name:          sheet.name || existing.name || productId,
                    category:      'element',
                    source:        'brand-product-sheet',
                    aspect_ratio:  sheet.aspect_ratio,
                    image:         sheet.url,
                    startImage:    sheet.url,
                    thumbnail:     sheet.url,
                    description:   existing.description
                        || `${sheet.name} — product sheet at ${sheet.aspect_ratio}. Loaded from saved brand.`,
                };
            });
            const brandLogoCharacters = logoEntries.map(logo => {
                const logoId = logo.id || `${brand?.id || 'brand'}-logo`;
                const existing = existingBrandLogosById.get(logoId) || {};
                const logoName = logo.name
                    || existing.name
                    || (brand?.name ? `${brand.name} Logo` : 'Brand Logo');
                return {
                    ...existing,
                    id:          existing.id || `brand-logo-${logoId}`,
                    logo_id:     logoId,
                    name:        logoName,
                    category:    'element',
                    source:      'brand-logo',
                    image:       logo.url,
                    startImage:  logo.url,
                    thumbnail:   logo.url,
                    description: existing.description
                        || `${logoName} — brand logo loaded from saved brand.`,
                };
            });
            // Merge: keep any non-brand characters (manual + preserved brand
            // entries), then push new/updated brand-product-sheet entries.
            //
            // Feb 20 2026 — Also drop any LLM-generated elements whose name
            // fuzzy-matches a brand-product-sheet OR brand-logo name
            // (token-based; see `utils/brandProductNameMatch.js` — "Flyer
            // 14T Bicycle" collapses into "Flyer 14T", "gro Logo" collapses
            // into the injected logo entry). The fetched brand asset
            // supersedes the LLM duplicate. We absorb the LLM's description
            // into the brand entry when it still has the auto-generated
            // placeholder copy.
            const brandAssetNames = [...brandCharacters, ...brandLogoCharacters]
                .map(c => c.name)
                .filter(Boolean);
            const llmDescByBrandName = new Map();
            const mergedCharactersMap = new Map();
            for (const c of nonBrandChars) {
                if (c?.source === 'brand-product-sheet' || c?.source === 'brand-logo') continue; // will be re-added below
                const match = findMatchingBrandSheetName(c?.name, brandAssetNames);
                if (match) {
                    if (c.description && !llmDescByBrandName.has(match)) {
                        llmDescByBrandName.set(match, c.description);
                    }
                    console.log(`🧹 Dropping duplicate element "${c.name}" — superseded by brand asset "${match}"`);
                    continue; // drop LLM duplicate
                }
                mergedCharactersMap.set(c.id, c);
            }
            const absorbInto = (c) => {
                const absorbedDesc = c.name ? llmDescByBrandName.get(c.name) : null;
                const isPlaceholder = typeof c.description === 'string'
                    && /(product sheet at .*\. Loaded from saved brand\.|brand logo loaded from saved brand\.)/i.test(c.description);
                return (absorbedDesc && (isPlaceholder || !c.description))
                    ? { ...c, description: absorbedDesc }
                    : c;
            };
            for (const c of brandCharacters)     mergedCharactersMap.set(c.id, absorbInto(c));
            for (const c of brandLogoCharacters) mergedCharactersMap.set(c.id, absorbInto(c));
            const nextCharacters = Array.from(mergedCharactersMap.values());

            // Feb 2026 — Stamp the full brand context onto the project so
            // downstream steps (concept, script, co-pilot) can inject rich
            // brand + product details into the LLM prompt (not just the
            // brand ID). Without this, the LLM only sees the brand name
            // and produces generic-looking concepts.
            //
            // Feb 13 2026 — `brandContext.products[].images[]` now stores
            // the SINGLE product sheet URL for the project's aspect ratio
            // (from castAndWorld.products) instead of the raw multi-image
            // gallery. This keeps the LLM prompt anchored on the same
            // sheet the user sees in Cast & World.
            const sheetByProductId = new Map(sheetEntries.map(s => [s.product_id, s.url]));
            const brandContext = brand ? {
                brand: {
                    id:          brand.id,
                    name:        brand.name,
                    description: brand.description || brand.desc || '',
                    tagline:     brand.tagline || brand.profile?.tagline || '',
                    profile:     brand.profile || null,
                },
                products: (pickedProductsFull || []).map((p) => {
                    const sheetUrl = sheetByProductId.get(p.id);
                    return {
                        id:          p.id,
                        name:        p.name,
                        description: p.description || p.desc || '',
                        price:       p.price,
                        // Prefer the sheet URL when available; otherwise
                        // fall back to the raw product images (so text
                        // context is still meaningful even if the sheet
                        // fetch failed).
                        images: sheetUrl
                            ? [sheetUrl]
                            : (Array.isArray(p.images)
                                ? p.images
                                    .map((img) => typeof img === 'string' ? img : (img?.url || null))
                                    .filter(Boolean)
                                : []),
                        has_product_sheet: !!sheetUrl,
                    };
                }),
                services: (pickedServicesFull || []).map((s) => ({
                    id:          s.id,
                    name:        s.name,
                    description: s.description || s.desc || '',
                })),
                productsMissingSheet: Array.isArray(productsMissingSheet) ? productsMissingSheet : [],
            } : null;
            return {
                ...prev,
                brandId:    brandId    || null,
                productIds: productIds || [],
                serviceIds: serviceIds || [],
                brandName:  brand?.name && !prev.brandName ? brand.name : prev.brandName,
                // Preserve the existing flat `assets` array as-is — do NOT
                // reshape it, or backendService.saveProject will throw
                // "e.assets is not iterable".
                assets:     Array.isArray(prev.assets) ? prev.assets : [],
                brandContext,
                // Feb 13 2026 — Product sheets loaded as Element entries
                // in the Cast & World "Props & Elements" tab.
                characters: nextCharacters,
                industryId:               nextIndustryId,
                industryAutoFromBrand:    nextIndustryAutoFromBrand,
                lastModified: Date.now(),
            };
        });
    };

    // Phase 6 (Feb 2026) — Apply a Co-pilot-proposed scoped action. The
    // action has already been validated against the per-step allow-list
    // inside CoPilotPanel; this function performs the actual mutation.
    // Throws if the action type is unknown so the panel can surface an
    // error toast.
    const handleCoPilotAction = (action) => {
        if (!activeProject || !action || !action.type) return;
        const { type, payload = {} } = action;
        let patch = null;

        if (type === 'project.rename') {
            patch = { name: payload.value };
        } else if (type === 'project.set_duration') {
            patch = { duration: payload.value };
        } else if (type === 'project.set_aspect_ratio') {
            patch = { aspectRatio: payload.value };
        } else if (type === 'project.set_tier' || type === 'tier.switch') {
            patch = { tier: payload.value };
        } else if (type === 'script.replace') {
            patch = { script: payload.value };
        } else if (type === 'script.trim' || type === 'script.change_tone') {
            // Trim/tone require an LLM rewrite — for now, the LLM should
            // have included `script.replace` instead. If we ever receive
            // `script.trim` without a replacement value, the spec doc
            // tells the admin to convert it into `script.replace`.
            throw new Error('This action needs a replacement script from the Co-pilot. Ask again.');
        } else if (type === 'branding.toggle_logo') {
            patch = { branding: { ...(activeProject.branding || {}), logoEnabled: payload.enabled } };
        } else if (type === 'branding.toggle_outro') {
            patch = { branding: { ...(activeProject.branding || {}), outroEnabled: payload.enabled } };
        } else if (type === 'branding.set_logo_position') {
            patch = { branding: { ...(activeProject.branding || {}), logoPosition: payload.value } };
        } else if (type === 'characters.add_narrator') {
            const newChar = {
                id: uuidv4(), name: 'Narrator', description: '',
                category: 'narrator', costumes: [], image: null,
            };
            patch = { characters: [...(activeProject.characters || []), newChar] };
        } else if (type === 'characters.add_character') {
            const newChar = {
                id: uuidv4(),
                name: payload.name,
                description: payload.description || '',
                category: 'character',
                costumes: [], image: null,
            };
            patch = { characters: [...(activeProject.characters || []), newChar] };
        } else if (type === 'characters.remove') {
            patch = { characters: (activeProject.characters || []).filter(c => c.id !== payload.id) };
        } else if (type === 'characters.update') {
            patch = {
                characters: (activeProject.characters || []).map(c => (
                    c.id === payload.id
                        ? { ...c, ...(payload.name ? { name: payload.name } : {}), ...(payload.description ? { description: payload.description } : {}) }
                        : c
                ))
            };
        } else {
            throw new Error(`Unsupported action: ${type}`);
        }

        if (patch) {
            setActiveProject(prev => prev ? { ...prev, ...patch, lastModified: Date.now() } : prev);
        }
    };

    const handleCharactersSubmit = async (characters) => {
        if (!activeProject) return;
        if (!activeProject.scenes || activeProject.scenes.length === 0) {
            if (!activeProject.script || activeProject.script.trim() === '') {
                const blankScene = { 
                    id: `scene-${Date.now()}`, 
                    shots: [{ 
                        id: `shot-${Date.now()}`, 
                        description: 'New shot', 
                        startFramePrompt: '', 
                        endFramePrompt: '', 
                        narration: '', 
                        characterIds: [], 
                        elementIds: [] 
                    }] 
                };
                setActiveProject({ ...activeProject, characters, scenes: [blankScene], lastModified: Date.now(), currentStep: 'branding' });
                setStep('branding');
                return;
            }
            setGenerationState('storyboarding');
            try {
                const storyboardResult = await generateStoryboard({ ...activeProject, characters }, setGenerationProgress);
                
                // Extract scenes and track credits
                const storyboardData = storyboardResult?.scenes || storyboardResult;
                if (storyboardResult?.credits_used) {
                    handleTrackCreditsUsed(storyboardResult.credits_used);
                }
                
                const scenesArray = Array.isArray(storyboardData) ? storyboardData : [];
                const scenesWithIds = scenesArray.map((sData, sIdx) => ({
                    id: `scene-${sIdx}-${Date.now()}`,
                    shots: sData.shots.map((shData, shIdx) => {
                        const { characterIds, elementIds } = mapEntitiesToShot(shData, characters);
                        // Handle both 'dialogue' (singular) and 'dialogues' (plural) from backend
                        const rawDialogues = Array.isArray(shData.dialogues) ? shData.dialogues 
                            : Array.isArray(shData.dialogue) ? shData.dialogue 
                            : [];
                        return {
                            id: `shot-${sIdx}-${shIdx}-${Date.now()}`,
                            description: shData.description,
                            startFramePrompt: shData.startFramePrompt,
                            endFramePrompt: shData.endFramePrompt,
                            narration: shData.narration,
                            characterIds,
                            elementIds,
                            dialogues: rawDialogues.map((d, dIdx) => {
                                const charName = (d.characterName || d.character_name || d.character || '').toLowerCase();
                                // Fuzzy match: exact name, or first name match
                                const matchedChar = characters.find(c => {
                                    const cName = (c.name || '').toLowerCase();
                                    if (cName === charName) return true;
                                    const firstName = cName.split(/\s+/)[0];
                                    const dFirstName = charName.split(/\s+/)[0];
                                    return firstName.length > 2 && (cName.includes(charName) || charName.includes(firstName) || firstName === dFirstName);
                                });
                                return { 
                                    characterId: matchedChar?.id, 
                                    characterName: d.characterName || d.character_name || d.character || '',
                                    text: d.text || d.dialogue || '',
                                    order: d.order ?? dIdx,
                                    startTime: d.startTime || d.start_time,
                                    endTime: d.endTime || d.end_time,
                                };
                            }).filter(d => d.characterId && d.text)
                        };
                    })
                }));
                const updatedAfterStoryboardGen = { ...activeProject, creditsUsed: (activeProject?.creditsUsed || 0) + (Number(storyboardResult?.credits_used) || 0), characters, scenes: scenesWithIds, lastModified: Date.now(), currentStep: 'branding' };
                setActiveProject(updatedAfterStoryboardGen);
                setStep('branding');
                setTimeout(() => autoSaveProject({ reason: 'characters→branding', projectOverride: updatedAfterStoryboardGen }), 0);
            } catch (err) {
                setError("Failed to generate storyboard.");
                setStep('branding');
            } finally {
                setGenerationState('idle');
            }
        } else {
            const updatedAfterCharacters = { ...activeProject, characters, lastModified: Date.now(), currentStep: 'branding' };
            setActiveProject(updatedAfterCharacters);
            setStep('branding');
            setTimeout(() => autoSaveProject({ reason: 'characters→branding', projectOverride: updatedAfterCharacters }), 0);
        }
    };

    const handleBrandingSubmit = (branding) => {
        if (!activeProject) return;
        const updated = { ...activeProject, branding, lastModified: Date.now(), currentStep: 'tier' };
        setActiveProject(updated);
        setStep('tier');
        setTimeout(() => autoSaveProject({ reason: 'branding→tier', projectOverride: updated }), 0);
    };

    // Tier Selection submits with the (possibly enriched) characters + the
    // freshly-generated scenes. Persist tier + selectedModels and route the
    // user straight into the Storyboard step (or AutoPilot if requested).
    const handleTierSelectionSubmit = ({ characters, scenes, tier, selectedModels, autoPilot }) => {
        if (!activeProject) return;
        const nextStep = autoPilot ? 'auto-pilot' : 'storyboard';
        const updated = {
            ...activeProject,
            characters: characters || activeProject.characters,
            scenes: scenes || activeProject.scenes,
            tier,
            selectedModels,
            modelPreferences: { ...(activeProject.modelPreferences || {}), selectedModels },
            lastModified: Date.now(),
            currentStep: nextStep,
        };
        setActiveProject(updated);
        setStep(nextStep);
        setTimeout(() => autoSaveProject({ reason: `tier→${nextStep}`, projectOverride: updated }), 0);
    };

    const handleAutoPilotComplete = (finalProject) => {
        if (!finalProject) return;
        const updated = { ...activeProject, ...finalProject, lastModified: Date.now(), currentStep: 'studio' };
        setActiveProject(updated);
        setStep('studio');
        setTimeout(() => autoSaveProject({ reason: 'autopilot-complete', projectOverride: updated }), 0);
    };

    const handleStoryboardSubmit = async (scenes) => {
        if (!activeProject) return;
        setGenerationProgress('Preparing Studio...');
        setGenerationState('transcribing'); 
        try {
            const timeline = await createTimelineFromScenes(scenes, activeProject.timeline, activeProject.branding);
            const updatedAfterStudio = { ...activeProject, scenes, timeline, lastModified: Date.now(), currentStep: 'studio' };
            setActiveProject(updatedAfterStudio);
            setStep('studio');
            // Stage 6: auto-save on entering Studio Editor
            setTimeout(() => autoSaveProject({ reason: 'storyboard→studio', projectOverride: updatedAfterStudio }), 0);
        } catch (e) {
            setStep('studio');
        } finally {
            setGenerationState('idle');
        }
    };

    const handleProjectUpdate = (updatedProject) => {
        // Never let a child-supplied project snapshot regress the observer-
        // maintained `creditsUsed`. Children frequently spread a stale
        // `projectData` → add/update something → call onProjectUpdate with
        // it. Their creditsUsed is whatever it was when the prop was last
        // read, which is usually BEFORE the user.credits observer fired.
        setActiveProject(prev => {
            if (!updatedProject) return prev;
            const prevCredits = prev?.creditsUsed || 0;
            const nextCredits = updatedProject.creditsUsed || 0;
            const prevJobs = prev?.jobIds || [];
            const nextJobs = updatedProject.jobIds || [];
            return {
                ...updatedProject,
                creditsUsed: Math.max(prevCredits, nextCredits),
                jobIds: Array.from(new Set([...prevJobs, ...nextJobs]))
            };
        });
    };

    const handleExportProject = () => {
        if (!activeProject) return;
        const blob = new Blob([JSON.stringify(activeProject, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeProject.name || 'project'}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleUserClick = () => {
        const email = prompt("Enter your email:", user?.email);
        if (email && email.trim()) {
            // TODO: Implement setUserEmail if needed
            console.log('User email changed to:', email.trim());
        }
    };

    const handleImportProject = (file) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const content = e.target?.result;
                const project = JSON.parse(content);
                const importedProject = {
                    ...project,
                    id: uuidv4(),
                    projectId: undefined,
                    lastModified: Date.now()
                };
                setActiveProject(importedProject);
                await saveProjectToLocal(importedProject);
                setStep('project');
            } catch (error) {
                console.error("Error importing project:", error);
                alert("Failed to import project.");
            }
        };
        reader.readAsText(file);
    };

    // ─── Session Not Found Screen ───
    if (!userLoading && (!user?.isAuthenticated)) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-[#0a0a0f] text-white" data-testid="session-not-found">
                <div className="flex flex-col items-center gap-6 max-w-md text-center px-6">
                    {/* Logo */}
                    <div className="flex items-center gap-2.5">
                        <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                            </svg>
                        </div>
                        <span className="text-2xl font-bold tracking-tight">Studio Pro</span>
                    </div>

                    {/* Session message */}
                    <h2 className="text-xl font-bold text-white">Session not found</h2>
                    <p className="text-gray-400 text-sm leading-relaxed">
                        Please log into your LoopAI account first, then navigate back to Studio Pro.
                    </p>

                    {/* CTA button */}
                    <a href="https://app.loopai.in"
                       target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
                       data-testid="go-to-loopai-btn">
                        Go to LoopAI
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                        </svg>
                    </a>
                </div>
            </div>
        );
    }

    if (generationState !== 'idle' && generationState !== 'error') {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-[#0a0a0f] text-white">
                <LoadingSpinner className="w-16 h-16 mb-6 text-purple-500" />
                <h2 className="text-2xl font-bold mb-2">Processing...</h2>
                <p className="text-gray-400 mb-8">{generationProgress || 'Creating Magic...'}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-[#0a0a0f] text-white">
            {/* Feb 2026 (Phase 2) — global modal that gates every video-
                generation fallback with a user approval dialog. Listens
                via the videoFallbackApprovalBroker; safe to mount once
                at the app root because only one dialog can be active. */}
            <VideoFallbackApprovalDialog />
            <header className="h-16 border-b border-gray-800 flex items-center justify-between px-6 shrink-0 bg-[#101018]">
                <div className="flex items-center gap-4">
                    <a href="https://app.loopai.in/dashboard"
                       className="flex items-center gap-1.5 text-gray-400 hover:text-white text-xs font-medium transition-colors"
                       data-testid="back-to-dashboard-btn">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                        Back to Dashboard
                    </a>
                    <div className="w-px h-6 bg-gray-700" />
                    <div className="flex items-center cursor-pointer" onClick={() => setStep('dashboard')}>
                        <MagicWandIcon className="w-6 h-6 text-purple-500 mr-2" />
                        <h1 className="text-xl font-bold tracking-tight">Studio Pro</h1>
                    </div>
                </div>
                <div className="flex items-center space-x-6">
                    {activeProject && step !== 'dashboard' && (
                        <>
                            {/* Auto-save indicator */}
                            <div className="text-xs font-medium" data-testid="autosave-indicator">
                                {saveIndicator === 'saving' && (
                                    <span className="text-gray-400 flex items-center gap-1.5">
                                        <LoadingSpinner className="w-3 h-3" />Saving…
                                    </span>
                                )}
                                {saveIndicator === 'saved' && (
                                    <span className="text-emerald-400">Saved just now</span>
                                )}
                                {saveIndicator === 'error' && (
                                    <span className="text-amber-400" title="Auto-save hit an issue — click Save Project to retry">Unsaved changes</span>
                                )}
                            </div>
                            <button onClick={handleExportProject} className="text-gray-400 hover:text-white transition-colors" title="Export Project to JSON">
                                <DownloadIcon className="w-5 h-5" />
                            </button>
                            <button onClick={handleSaveProject} disabled={isSaving} className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded-md transition-colors disabled:opacity-50">
                                {isSaving ? <LoadingSpinner className="w-4 h-4" /> : <SaveIcon className="w-4 h-4" />}
                                <span className="text-sm font-medium">{isSaving ? 'Saving...' : 'Save Project'}</span>
                            </button>
                        </>
                    )}
                    <div className="flex items-center space-x-4">
                        {activeProject && step !== 'dashboard' && (
                            <div className="flex items-center space-x-2 bg-purple-900/30 px-3 py-1 rounded-full border border-purple-700/50">
                                <span className="text-xs font-medium text-purple-400">PROJECT</span>
                                <span className="text-sm font-bold text-purple-300">{(activeProject.creditsUsed || 0).toLocaleString()}</span>
                            </div>
                        )}
                        <div 
                            className={`flex items-center space-x-2 px-3 py-1 rounded-full border cursor-pointer transition-colors hover:opacity-80 ${
                                (user?.credits || 0) < 200 
                                    ? 'bg-red-900/30 border-red-500/50' 
                                    : 'bg-gray-800/50 border-gray-700'
                            }`}
                            onClick={() => setShowCreditsModal(true)}
                            title="Click to add credits"
                            data-testid="credits-badge"
                        >
                            <span className={`text-xs font-medium ${(user?.credits || 0) < 200 ? 'text-red-400' : 'text-gray-400'}`}>CREDITS</span>
                            <span className={`text-sm font-bold ${(user?.credits || 0) < 200 ? 'text-red-300' : 'text-green-400'}`}>{user?.credits?.toLocaleString() || 0}</span>
                            <svg className={`w-3 h-3 ${(user?.credits || 0) < 200 ? 'text-red-400' : 'text-gray-500'}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 cursor-pointer hover:bg-gray-800 rounded-full p-1 pr-3 transition-colors" onClick={handleUserClick}>
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-xs font-bold">{user?.name?.charAt(0)?.toUpperCase() || "G"}</div>
                        <span className="text-xs text-gray-400 hidden md:inline max-w-[100px] truncate">{user?.email || "guest@loopai.com"}</span>
                    </div>
                </div>
            </header>

            <main className="flex-grow overflow-hidden relative flex flex-col">
                {/* Phase 1 — Always-visible 7-step progress rail.
                    Rendered ONLY for the linear wizard screens. Storyboard,
                    Auto-Pilot, Studio and Dashboard intentionally hide it so
                    they get their full viewport back. */}
                {activeProject && ['project', 'script', 'concept', 'characters', 'branding', 'tier'].includes(step) && (
                    <div className="flex-shrink-0 border-b border-white/5 bg-[#0a0a0f]/60 backdrop-blur-sm">
                        <WizardProgressRail
                            currentAppStep={step}
                            onJumpTo={(target) => setStep(target)}
                        />
                    </div>
                )}
                {/* Phase 6.1 (Feb 2026): wizard content + persistent Co-pilot
                    rail share a horizontal flex row. The rail is mounted as
                    a sibling column (not an overlay) so it's always visible
                    across all 7 wizard steps. It collapses to a 48px strip
                    when minimised — never disappears. */}
                <div className="flex-1 overflow-hidden relative flex flex-row">
                <div className="flex-1 overflow-hidden relative">
                {step === 'dashboard' && (
                    <ProjectDashboard 
                        projects={projects}
                        onCreate={handleCreateProject}
                        onLoad={handleLoadProject}
                        onDelete={handleDeleteProject}
                        onImport={handleImportProject}
                        onRefresh={loadProjects}
                        isLoading={isLoadingProjects}
                        userName={user?.name || user?.email?.split('@')[0] || 'Creator'}
                        credits={user?.credits || 0}
                        onUpdateThumbnail={async (projectId, dataUrl) => {
                            // Update the project list in state
                            setProjects(prev => prev.map(p => p.id === projectId ? { ...p, thumbnail: dataUrl } : p));
                            // Persist thumbnail in IndexedDB
                            try {
                                const fullProject = await loadProjectFromIndexedDB(projectId);
                                if (fullProject) {
                                    fullProject.thumbnail = dataUrl;
                                    await saveProjectToIndexedDB(fullProject);
                                }
                            } catch (e) { console.warn('Failed to save thumbnail:', e); }
                            // Update localStorage metadata
                            const listKey = `projects_list_${user?.email || 'guest'}`;
                            try {
                                const list = JSON.parse(localStorage.getItem(listKey) || '[]');
                                const idx = list.findIndex(p => p.id === projectId);
                                if (idx >= 0) { list[idx].thumbnail = dataUrl; localStorage.setItem(listKey, JSON.stringify(list)); }
                            } catch { /* ignore */ }
                        }}
                    />
                )}
                {step === 'project' && activeProject && <ProjectSetup initialData={activeProject} onSubmit={handleProjectSetupSubmit} />}
                {step === 'models' && activeProject && <ModelSelection initialData={activeProject} onSubmit={handleModelSelectionSubmit} onBack={() => setStep('project')} />}
                {step === 'script' && activeProject && <ScriptInput initialData={activeProject} onSubmit={handleScriptSubmit} onBack={() => setStep('models')} error={error} onDeductCredits={handleDeductCredits} onBrandScopeApply={handleBrandScopeApply} />}
                {step === 'concept' && activeProject && (
                    <ConceptStep
                        initialData={activeProject}
                        onScriptReady={handleConceptScriptReady}
                        onBack={() => setStep('script')}
                    />
                )}
                {step === 'characters' && activeProject && (
                    <CharacterSelection 
                        initialData={activeProject}
                        onSubmit={handleCharactersSubmit}
                        onBack={() => setStep('script')}
                        error={error}
                        onDeductCredits={handleDeductCredits}
                        onTrackCreditsUsed={handleTrackCreditsUsed}
                        onRefreshCredits={refreshCredits}
                        onSaveMedia={handleSaveMedia}
                        onProjectUpdate={handleProjectUpdate}
                        onAutoSave={autoSaveProject}
                    />
                )}
                {step === 'branding' && activeProject && (
                    <Branding
                        initialData={activeProject}
                        onSubmit={handleBrandingSubmit}
                        onBack={() => setStep('characters')}
                        onAutoSave={autoSaveProject}
                    />
                )}
                {step === 'tier' && activeProject && (
                    <TierSelection
                        initialData={{ ...activeProject, creditsUsed }}
                        onSubmit={handleTierSelectionSubmit}
                        onBack={() => setStep('branding')}
                        onTrackCreditsUsed={handleTrackCreditsUsed}
                        onRefreshCredits={refreshCredits}
                        onSaveMedia={handleSaveMedia}
                    />
                )}
                {step === 'auto-pilot' && activeProject && (
                    <AutoPilot
                        initialData={activeProject}
                        onProjectUpdate={handleProjectUpdate}
                        onAutoSave={autoSaveProject}
                        onBack={() => setStep('tier')}
                        onTrackCreditsUsed={handleTrackCreditsUsed}
                        onRefreshCredits={refreshCredits}
                        onSaveMedia={handleSaveMedia}
                        onDeductCredits={handleDeductCredits}
                        createTimelineFromScenes={createTimelineFromScenes}
                        onComplete={handleAutoPilotComplete}
                        onBackToDashboard={() => { setActiveProject(null); setStep('dashboard'); }}
                    />
                )}
                {step === 'storyboard' && activeProject && (
                    <Storyboard
                        projectData={activeProject}
                        onSubmit={handleStoryboardSubmit}
                        onProjectUpdate={handleProjectUpdate}
                        onBack={() => setStep('tier')}
                        onDeductCredits={handleDeductCredits}
                        onTrackCreditsUsed={handleTrackCreditsUsed}
                        onRefreshCredits={refreshCredits}
                        onSaveMedia={handleSaveMedia}
                        onAutoSave={autoSaveProject}
                    />
                )}
                {step === 'studio' && activeProject && <Studio projectData={activeProject} onProjectUpdate={handleProjectUpdate} onBack={() => setStep('storyboard')} onGoToDashboard={() => { setActiveProject(null); setStep('dashboard'); }} onAutoSave={autoSaveProject} />}
                </div>

                {/* Phase 6.1 (Feb 2026) — Persistent Co-pilot right rail.
                    Mounted as a sibling column to the step content so it's
                    always visible across the 7 wizard steps. Collapses to
                    a 48px strip; never hidden. */}
                {step !== 'dashboard' && activeProject && (
                    <CoPilotPanel
                        isOpen={coPilotOpen}
                        onClose={() => setCoPilotOpen(v => !v)}
                        currentAppStep={step}
                        project={activeProject}
                        onApplyAction={handleCoPilotAction}
                    />
                )}
                </div>
            </main>

            {/* Credits Modal — iframe overlay */}
            {showCreditsModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="credits-modal-overlay">
                    <div className="relative w-[90vw] max-w-[800px] h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="credits-modal">
                        <button
                            onClick={async () => {
                                setShowCreditsModal(false);
                                try {
                                    const serverCredits = await getCredits();
                                    if (serverCredits > 0) updateCredits(serverCredits);
                                } catch { /* ignore */ }
                            }}
                            className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-gray-800 text-white hover:bg-gray-700 transition-colors shadow-lg"
                            data-testid="credits-modal-close"
                            title="Close"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                        <iframe
                            src="https://app.loopai.in/credits"
                            title="Credits Management"
                            className="w-full h-full border-0"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;
